import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatCircleDots, PaperPlaneRight, Trash, Microphone, Stop, Paperclip, X, CircleNotch, BookmarkSimple, SpeakerHigh, Pause, PencilSimple, ArrowsClockwise } from "@phosphor-icons/react";
import SavePlanDialog from "@/components/SavePlanDialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const stripMarkers = (t) => (t || "").replace(/\s*\[\[(MEMORY|SETTING|IMAGE):[^\]]*\]\]/g, "");

const Md = ({ children }) => (
  <div className="md-body text-sm leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:my-1 [&_ol]:pl-4 [&_ol]:list-decimal [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold [&_table]:text-xs [&_table]:my-2 [&_td]:border [&_td]:border-white/10 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-white/10 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-white/[0.06] [&_a]:text-[#4dabf5] [&_a]:underline [&_code]:bg-black/40 [&_code]:px-1 [&_code]:rounded [&_code]:text-[12px] [&_pre]:bg-black/50 [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_blockquote]:border-l-2 [&_blockquote]:border-white/20 [&_blockquote]:pl-2 [&_blockquote]:text-muted-foreground">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
  </div>
);

export default function AIChatCard({ voice = false, attach = false, title = "Чат с AI-тренером", threadId = null, tts = false, autoVoice = false, onMeta }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [attached, setAttached] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [extracting, setExtracting] = useState(null);
  const boxRef = useRef(null);
  const esRef = useRef(null);
  const recRef = useRef(null);
  const fileRef = useRef(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const audioRef = useRef(null);

  const speakMsg = async (text, i) => {
    if (speakingIdx === i) { audioRef.current?.pause(); setSpeakingIdx(null); return; }
    try {
      audioRef.current?.pause();
      setSpeakingIdx(i);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/tts`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: stripMarkers(text) }),
      });
      if (!r.ok) throw new Error();
      const audio = new Audio(URL.createObjectURL(await r.blob()));
      audio.onended = () => setSpeakingIdx(null);
      audioRef.current = audio;
      audio.play();
    } catch { setSpeakingIdx(null); toast.error("Не удалось озвучить"); }
  };

  useEffect(() => {
    setMessages([]);
    api.get(`/ai/chat${threadId ? `?thread_id=${threadId}` : ""}`).then((r) => {
      setMessages(r.data);
      if (r.data.length) setOpen(true);
    }).catch(() => {});
    return () => { esRef.current?.close(); audioRef.current?.pause(); };
  }, [threadId]);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (qArg) => {
    const q = (qArg || input).trim();
    if (!q || streaming) return;
    setInput("");
    setOpen(true);
    setStreaming(true);
    const fileIds = attached.map((a) => a.id);
    const display = q + (attached.length ? `\n📎 ${attached.map((a) => a.name).join(", ")}` : "");
    setAttached([]);
    setMessages((m) => [...m, { role: "user", content: display }, { role: "assistant", content: "" }]);
    if (voice && !fileIds.length) {
      try {
        const { data: cmd } = await api.post("/assistant/command", { text: q });
        if (cmd.message) {
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", content: cmd.message, image_file_id: cmd.image_file_id || null };
            return c;
          });
          setStreaming(false);
          return;
        }
      } catch {}
    }
    const es = new EventSource(
      `${process.env.REACT_APP_BACKEND_URL}/api/ai/chat/stream?q=${encodeURIComponent(q)}${fileIds.length ? `&files=${fileIds.join(",")}` : ""}${threadId ? `&thread_id=${threadId}` : ""}`,
      { withCredentials: true }
    );
    esRef.current = es;
    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.t) setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d.t };
          return c;
        });
        if (d.meta) {
          if (d.meta.memory_saved) toast.success(`🧠 Запомнил: ${d.meta.memory_saved.join("; ")}`);
          if (d.meta.settings_saved) toast.success("⚙️ Настройки бота обновлены");
          if (d.meta.action_done) toast.success(`✓ ${d.meta.action_done}`);
          if (d.meta.file_error) toast.error(d.meta.file_error);
          if (d.meta.image_file_id || d.meta.file) setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { ...c[c.length - 1],
              ...(d.meta.image_file_id ? { image_file_id: d.meta.image_file_id } : {}),
              ...(d.meta.file ? { file: d.meta.file } : {}) };
            return c;
          });
          onMeta?.(d.meta);
        }
        if (d.error) setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { ...c[c.length - 1], content: `Ошибка: ${d.error}` };
          return c;
        });
        if (d.done) {
          es.close(); setStreaming(false);
          if (autoVoice) setMessages((m) => {
            const last = m[m.length - 1];
            if (last?.role === "assistant" && last.content) speakMsg(last.content, m.length - 1);
            return m;
          });
        }
      } catch {}
    };
    es.onerror = () => { es.close(); setStreaming(false); };
  };

  const toggleRec = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: mime }), mime.includes("webm") ? "v.webm" : "v.mp4");
          const { data } = await api.post("/voice/transcribe", fd);
          const { data: cmd } = await api.post("/assistant/command", { text: data.text });
          if (cmd.message) {
            setOpen(true);
            setMessages((m) => [...m, { role: "user", content: data.text }, { role: "assistant", content: cmd.message, image_file_id: cmd.image_file_id || null }]);
          } else {
            send(data.text);
          }
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setProcessing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Нет доступа к микрофону — разрешите его в настройках браузера");
    }
  };

  const attachFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/assistant/files/upload", fd);
        setAttached((a) => [...a, { id: data.id, name: data.name, status: data.status }]);
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setUploading(false);
  };

  const extractPlan = async (content, i) => {
    setExtracting(i);
    try {
      const { data } = await api.post("/workouts/extract", { text: content });
      if (data.kind === "none") toast.info("В этом сообщении нет плана тренировки или блюда");
      else { setPlan(data); setPlanOpen(true); }
    } catch (err) { toast.error(formatApiError(err)); }
    setExtracting(null);
  };

  const editOrRegen = async (regen) => {
    if (streaming) return;
    try {
      const { data } = await api.delete(`/ai/chat/last${threadId ? `?thread_id=${threadId}` : ""}`);
      setMessages((m) => m.slice(0, -2));
      if (regen && data.q) send(data.q);
      else if (data.q) setInput(data.q);
    } catch {}
  };

  const clear = () => {
    api.delete(`/ai/chat${threadId ? `?thread_id=${threadId}` : ""}`).then(() => setMessages([]));
  };

  const suggestions = ["Почему у меня упало восстановление?", "Составь план тренировок на неделю", "Как мне улучшить сон?"];

  return (
    <div className="glass-card border-[#0084ff]/30 bg-gradient-to-br from-[#0084ff]/10 to-transparent p-4 sm:p-6" data-testid="ai-chat-card">
      <div className="flex items-center gap-2 flex-wrap">
        <ChatCircleDots size={20} weight="fill" className="text-[#0084ff]" />
        <h3 className="font-heading font-semibold text-lg">{title}</h3>
        {messages.length > 0 && (
          <button onClick={clear} data-testid="ai-chat-clear-button"
            className="ml-auto p-1.5 text-muted-foreground hover:text-destructive transition-colors">
            <Trash size={16} />
          </button>
        )}
      </div>
      {!open && (
        <div className="flex gap-2 flex-wrap mt-3">
          {suggestions.map((s) => (
            <button key={s} onClick={() => { setInput(s); }} data-testid="ai-chat-suggestion"
              className="text-xs px-3 py-1.5 rounded-full bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}
      {messages.length > 0 && (
        <div ref={boxRef} className="mt-3 max-h-80 overflow-y-auto space-y-3 pr-1" data-testid="ai-chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-[#0084ff]/20 text-foreground whitespace-pre-wrap" : "bg-secondary/60"}`}>
                {m.role === "assistant" ? <Md>{stripMarkers(m.content)}</Md> : stripMarkers(m.content)}
                {m.role === "user" && i === messages.length - 2 && !streaming && (
                  <button onClick={() => editOrRegen(false)} data-testid={`edit-msg-button-${i}`} title="Изменить вопрос"
                    className="flex items-center gap-1 mt-1.5 text-[11px] text-muted-foreground hover:text-foreground">
                    <PencilSimple size={11} /> Изменить
                  </button>
                )}
                {m.image_file_id && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/assistant/files/${m.image_file_id}/download`} target="_blank" rel="noreferrer">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assistant/files/${m.image_file_id}/download`}
                      alt="" className="rounded-lg max-h-64 mt-2 border border-border/60" loading="lazy"
                      data-testid={`ai-chat-image-${i}`} />
                  </a>
                )}
                {m.file && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/assistant/files/${m.file.id}/download`} target="_blank" rel="noreferrer"
                    data-testid={`ai-chat-file-${i}`}
                    className="flex items-center gap-1.5 mt-2 px-3 py-2 rounded-lg bg-[#00c9a7]/15 text-[#00c9a7] text-xs font-bold ring-1 ring-[#00c9a7]/30 hover:brightness-110 w-fit">
                    ⬇ Скачать {m.file.name}
                  </a>
                )}
                {streaming && i === messages.length - 1 && m.role === "assistant" && (
                  <span className="inline-block w-2 h-4 bg-[#0084ff]/70 ml-0.5 animate-pulse align-text-bottom" />
                )}
                {m.role === "assistant" && m.content.length > 60 && !(streaming && i === messages.length - 1) && (
                  <div className="flex items-center gap-3 mt-2">
                    <button onClick={() => extractPlan(m.content, i)} disabled={extracting !== null}
                      data-testid={`save-plan-button-${i}`}
                      className="flex items-center gap-1 text-[11px] text-[#00c9a7] hover:underline disabled:opacity-50">
                      {extracting === i ? <CircleNotch size={12} className="animate-spin" /> : <BookmarkSimple size={12} weight="fill" />}
                      {extracting === i ? "Ищем план…" : "Сохранить план"}
                    </button>
                    {tts && (
                      <button onClick={() => speakMsg(m.content, i)} data-testid={`tts-play-button-${i}`}
                        className="flex items-center gap-1 text-[11px] text-[#0084ff] hover:underline">
                        {speakingIdx === i ? <Pause size={12} weight="fill" /> : <SpeakerHigh size={12} weight="fill" />}
                        {speakingIdx === i ? "Стоп" : "Озвучить"}
                      </button>
                    )}
                    {i === messages.length - 1 && (
                      <button onClick={() => editOrRegen(true)} data-testid={`regen-button-${i}`}
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                        <ArrowsClockwise size={12} /> Заново
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {attached.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-3" data-testid="ai-chat-attachments">
          {attached.map((a) => (
            <span key={a.id} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-primary/15 text-primary">
              <Paperclip size={11} /> <span className="truncate max-w-[140px]">{a.name}</span>
              <button onClick={() => setAttached((arr) => arr.filter((x) => x.id !== a.id))} data-testid={`attachment-remove-${a.id}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={processing ? "Распознаём речь…" : attached.length ? "Спросите о прикреплённых файлах…" : "Спросите о своих данных, сне, тренировках…"}
          data-testid="ai-chat-input" className="bg-background/60" />
        {attach && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={attachFiles} data-testid="ai-chat-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading || streaming} data-testid="ai-chat-attach-button"
              size="icon" variant="secondary" className="shrink-0" title="Прикрепить файл">
              {uploading ? <CircleNotch size={16} className="animate-spin" /> : <Paperclip size={16} />}
            </Button>
          </>
        )}        {voice && (
          <Button onClick={toggleRec} disabled={processing || streaming} data-testid="voice-mic-button" size="icon"
            variant={recording ? "destructive" : "secondary"}
            className={`shrink-0 ${recording ? "animate-pulse" : ""} ${processing ? "animate-pulse" : ""}`}>
            {recording ? <Stop size={16} weight="fill" /> : <Microphone size={16} weight="fill" />}
          </Button>
        )}
        <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ai-chat-send-button" size="icon" className="shrink-0">
          <PaperPlaneRight size={16} weight="fill" />
        </Button>
      </div>
      {recording && <p className="text-[11px] text-[#ff3b30] mt-2 animate-pulse" data-testid="voice-recording-hint">● Говорите… нажмите ⏹ чтобы закончить</p>}
      <SavePlanDialog open={planOpen} onOpenChange={setPlanOpen} plan={plan} />
    </div>
  );
}
